import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getPortalData, 
  getOrganizationBySlug,
  queryCollection,
  Organization, 
  Announcement, 
  Document,
  DOCUMENT_CATEGORIES 
} from '../lib/api';
import { PortalSettings } from '../types/portal';
import { PortalRenderer } from '../components/portal-templates';
import { 
  Newspaper, 
  FileText, 
  Download, 
  Calendar,
  Phone,
  EnvelopeSimple,
  Buildings,
  SignIn,
  House,
  CaretRight,
  MegaphoneSimple,
  Warning,
  Bell,
  CalendarBlank,
  FolderOpen
} from '@phosphor-icons/react';
import ImageGallery from '../components/ImageGallery';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Category icon mapping
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'news': return Newspaper;
    case 'important': return Warning;
    case 'reminder': return Bell;
    case 'event': return CalendarBlank;
    default: return MegaphoneSimple;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'news': return 'Nyhet';
    case 'important': return 'Viktigt';
    case 'reminder': return 'Påminnelse';
    case 'event': return 'Event';
    default: return 'Meddelande';
  }
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'news': return 'bg-blue-100 text-blue-800';
    case 'important': return 'bg-red-100 text-red-800';
    case 'reminder': return 'bg-amber-100 text-amber-800';
    case 'event': return 'bg-purple-100 text-purple-800';
    default: return 'bg-slate-100 text-slate-800';
  }
};

export default function PortalPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('news');
  const [useNewRenderer, setUseNewRenderer] = useState(false);

  useEffect(() => {
    loadPortalData();
  }, [slug, isAuthenticated]);

  const loadPortalData = async () => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      // First get organization by slug
      const org = await getOrganizationBySlug(slug);
      
      if (!org) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Check if organization has custom portal_settings in the new system
      try {
        const customSettings = await queryCollection<PortalSettings>('portal_settings', {
          organization_id: org.id
        });
        if (customSettings.length > 0) {
          // Use new PortalRenderer for organizations with custom settings
          setUseNewRenderer(true);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.log('No custom portal settings, using legacy portal');
      }

      // Fall back to legacy portal
      const data = await getPortalData(org.id, isAuthenticated);
      setOrganization(data.organization);
      setAnnouncements(data.announcements.sort((a, b) => 
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      ));
      setDocuments(data.documents.sort((a, b) => 
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      ));
    } catch (error) {
      console.error('Failed to load portal data:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // If using new renderer, delegate to PortalRenderer
  if (useNewRenderer && slug) {
    return <PortalRenderer slug={slug} isAuthenticated={isAuthenticated} />;
  }

  const getDocumentCategoryLabel = (category: string | undefined) => {
    if (!category) return 'Övrigt';
    const predefined = DOCUMENT_CATEGORIES.find(c => c.id === category);
    return predefined?.label || category;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Group documents by category
  const documentsByCategory = documents.reduce((acc, doc) => {
    const cat = doc.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <Buildings size={64} className="mx-auto text-slate-300 mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Föreningen hittades inte</h1>
            <p className="text-slate-600 mb-6">
              Kontrollera att du har rätt adress eller kontakta din bostadsrättsförening.
            </p>
            <Link to="/">
              <Button>Gå till startsidan</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const portalSettings = organization?.portal_settings || {};
  const bgColor = portalSettings.background_color || '#f8fafc';

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      {/* Header */}
      <header className="relative">
        {portalSettings.header_image ? (
          <div className="h-64 md:h-80 relative">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="max-w-5xl mx-auto flex items-end gap-4">
                {portalSettings.logo && (
                  <img 
                    src={portalSettings.logo} 
                    alt="Logo" 
                    className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white shadow-lg object-contain p-2"
                  />
                )}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                    {organization?.name}
                  </h1>
                  {portalSettings.description && (
                    <p className="text-white/90 mt-1 text-lg">{portalSettings.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-primary to-primary/80 py-12 md:py-16">
            <div className="max-w-5xl mx-auto px-6">
              <div className="flex items-center gap-4">
                {portalSettings.logo ? (
                  <img 
                    src={portalSettings.logo} 
                    alt="Logo" 
                    className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white shadow-lg object-contain p-2"
                  />
                ) : (
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white/20 flex items-center justify-center">
                    <Buildings size={40} className="text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    {organization?.name}
                  </h1>
                  {portalSettings.description && (
                    <p className="text-white/90 mt-1 text-lg">{portalSettings.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Login button */}
        <div className="absolute top-4 right-4">
          {isAuthenticated ? (
            <Link to="/dashboard">
              <Button variant="secondary" size="sm" className="shadow-lg">
                <House size={16} className="mr-2" />
                Till dashboarden
              </Button>
            </Link>
          ) : (
            <Link to="/">
              <Button variant="secondary" size="sm" className="shadow-lg">
                <SignIn size={16} className="mr-2" />
                Logga in
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="news" className="gap-2">
              <Newspaper size={16} />
              Nyheter
              {announcements.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                  {announcements.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText size={16} />
              Dokument
              {documents.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                  {documents.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2">
              <Phone size={16} />
              Kontakt
            </TabsTrigger>
          </TabsList>

          {/* News Tab */}
          <TabsContent value="news">
            {announcements.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Newspaper size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Inga nyheter just nu</p>
                  {!isAuthenticated && (
                    <p className="text-sm text-slate-400 mt-2">
                      Logga in för att se fler nyheter
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {announcements.map(announcement => {
                  const Icon = getCategoryIcon(announcement.category || 'news');
                  // Combine legacy image_url with images array
                  const legacyImage = (announcement as unknown as { image_url?: string }).image_url;
                  const allImages: string[] = [];
                  if (legacyImage) allImages.push(legacyImage);
                  if (announcement.images) {
                    announcement.images.forEach(img => {
                      if (!allImages.includes(img)) allImages.push(img);
                    });
                  }
                  const hasImages = allImages.length > 0;
                  
                  return (
                    <Card key={announcement.id} className="overflow-hidden hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          {!hasImages && (
                            <div className={`p-3 rounded-lg flex-shrink-0 ${getCategoryColor(announcement.category || 'news')}`}>
                              <Icon size={24} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(announcement.category || 'news')}`}>
                                {getCategoryLabel(announcement.category || 'news')}
                              </span>
                              {announcement.is_public && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                                  Publik
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">
                              {announcement.title}
                            </h3>
                            <p className="text-slate-600 whitespace-pre-wrap mb-4">
                              {announcement.content}
                            </p>
                            
                            {/* Image Gallery */}
                            {hasImages && (
                              <ImageGallery images={allImages} className="mb-4" />
                            )}
                            
                            {/* Attachments */}
                            {announcement.attachments && announcement.attachments.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-sm font-medium text-slate-700 mb-2">Bilagor:</p>
                                <div className="flex flex-wrap gap-2">
                                  {announcement.attachments.map((url) => {
                                    const fileName = url.split('/').pop() || 'Bilaga';
                                    return (
                                      <a
                                        key={url}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                                      >
                                        <Download size={14} />
                                        {fileName}
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(announcement.created_at)}
                              </span>
                              {announcement.created_by_name && (
                                <span>Av: {announcement.created_by_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            {documents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Inga dokument tillgängliga</p>
                  {!isAuthenticated && (
                    <p className="text-sm text-slate-400 mt-2">
                      Logga in för att se fler dokument
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(documentsByCategory).map(([category, docs]) => (
                  <Card key={category}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FolderOpen size={18} className="text-primary" />
                        {getDocumentCategoryLabel(category)}
                        <span className="text-sm font-normal text-slate-500">
                          ({docs.length})
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="divide-y">
                        {docs.map(doc => (
                          <div 
                            key={doc.id}
                            className="flex items-center justify-between py-3 group"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="p-2 bg-slate-100 rounded-lg">
                                <FileText size={20} className="text-slate-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                  {doc.name}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  {doc.file_size && (
                                    <span>{formatFileSize(doc.file_size)}</span>
                                  )}
                                  {doc.created_at && (
                                    <>
                                      <span>•</span>
                                      <span>{formatDate(doc.created_at)}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            {doc.file_url && (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              >
                                <Download size={20} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Buildings size={20} />
                  Kontakta {organization?.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {organization?.contact_email && (
                  <a 
                    href={`mailto:${organization.contact_email}`}
                    className="flex items-center gap-3 p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                  >
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <EnvelopeSimple size={24} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">E-post</p>
                      <p className="font-medium text-slate-900">{organization.contact_email}</p>
                    </div>
                    <CaretRight size={20} className="ml-auto text-slate-400" />
                  </a>
                )}
                
                {organization?.contact_phone && (
                  <a 
                    href={`tel:${organization.contact_phone}`}
                    className="flex items-center gap-3 p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                  >
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Phone size={24} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Telefon</p>
                      <p className="font-medium text-slate-900">{organization.contact_phone}</p>
                    </div>
                    <CaretRight size={20} className="ml-auto text-slate-400" />
                  </a>
                )}

                {!organization?.contact_email && !organization?.contact_phone && (
                  <p className="text-center text-slate-500 py-8">
                    Ingen kontaktinformation tillgänglig
                  </p>
                )}

                {isAuthenticated && (
                  <div className="pt-4 border-t">
                    <Link to="/issues/new">
                      <Button className="w-full">
                        Gör en felanmälan
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {organization?.name}</p>
          {!isAuthenticated && (
            <p className="mt-2">
              Boende? <Link to="/" className="text-primary hover:underline">Logga in</Link> för att se mer.
            </p>
          )}
        </footer>
      </main>
    </div>
  );
}
